/** Scan screen: camera QR (member `GP1…` or reservation `GPR1…`) or card number + PIN. */
import type { PosCredential } from "../api-types";
import { CameraUnavailableError, startQrScan, type ScanSession } from "../camera-qr-scanner";
import { errorBox, errorMessage, field, h } from "../dom-helpers";
import type { RenderedScreen, ScannerApp } from "../scanner-app-context";
import { staffApi } from "../staff-api-client";
import { strings } from "../strings";

/** True for GustoPass QR payloads: member tokens `GP1…` and reservation tokens `GPR1…`. */
export function isGustoQr(text: string): boolean {
  return text.startsWith("GP1") || text.startsWith("GPR1");
}

export function renderScan(app: ScannerApp): RenderedScreen {
  const error = errorBox();
  const video = h("video", { className: "camera", hidden: true, muted: true });
  const cameraStatus = h("p", { className: "muted", hidden: true });
  const cameraButton = h("button", { type: "button", className: "secondary" }, strings.scanCamera);
  const cardNumber = h("input", { name: "cardNumber", inputMode: "numeric", autocomplete: "off", maxLength: 14, placeholder: "000 0000 0000" });
  const pin = h("input", { name: "pin", inputMode: "numeric", autocomplete: "off", maxLength: 6 });
  const qrText = h("input", { name: "qrText", autocomplete: "off", spellcheck: "false" });
  const submit = h("button", { type: "submit", className: "primary" }, strings.scanSubmit);
  let session: ScanSession | null = null;
  let busy = false;

  const stopCamera = () => {
    session?.stop();
    session = null;
    video.hidden = true;
    cameraStatus.hidden = true;
    cameraButton.textContent = strings.scanCamera;
  };

  const lookup = async (credential: PosCredential) => {
    const till = app.till;
    if (!till || busy) return;
    busy = true;
    submit.disabled = true;
    error.show(null);
    try {
      const member = await staffApi.scan({ tillId: till.tillId, brandId: till.brandId, mallId: till.mallId, credential });
      app.navigate({ name: "bill", member });
    } catch (e) {
      if (!app.handleAuthError(e)) error.show(errorMessage(e));
    } finally {
      busy = false;
      submit.disabled = false;
    }
  };

  cameraButton.addEventListener("click", async () => {
    if (session) {
      stopCamera();
      return;
    }
    error.show(null);
    cameraStatus.textContent = strings.scanCameraStarting;
    cameraStatus.hidden = false;
    video.hidden = false;
    try {
      session = await startQrScan(video, (text) => {
        stopCamera();
        if (isGustoQr(text)) void lookup({ qrToken: text });
        else error.show(strings.scanUnknownCode);
      });
      cameraStatus.hidden = true;
      cameraButton.textContent = strings.scanStop;
    } catch (e) {
      stopCamera();
      error.show(e instanceof CameraUnavailableError ? strings.scanCameraUnavailable : strings.errorGeneric);
    }
  });

  const form = h(
    "form",
    {
      className: "card stack",
      onsubmit: (event: Event) => {
        event.preventDefault();
        const qr = qrText.value.trim();
        if (qr) {
          if (!isGustoQr(qr)) error.show(strings.scanUnknownCode);
          else void lookup({ qrToken: qr });
          return;
        }
        const number = cardNumber.value.replace(/\s+/g, "");
        if (!/^\d{11}$/.test(number) || !/^\d{6}$/.test(pin.value.trim())) {
          error.show(strings.scanInvalidCard);
          return;
        }
        void lookup({ cardNumber: number, pin: pin.value.trim() });
      },
    },
    h("h2", {}, strings.scanTitle),
    cameraButton,
    cameraStatus,
    video,
    h("h3", {}, strings.scanManual),
    h("div", { className: "row" }, field(strings.scanCardNumber, cardNumber), field(strings.scanPin, pin)),
    field(strings.scanQrText, qrText),
    error.el,
    submit,
  );
  return { el: form, dispose: stopCamera };
}
